import { type AuthorInfo, getCategoryAndSubcategory, getImageUrl } from "@serophin/nottldr-shared";
import { absoluteUrl } from "../../siteMeta";
import { ShareButton } from "../share/ShareButton";
import { AiBadge } from "./AiBadge";
import { AuthorProfileImage } from "./AuthorProfileImage";
import { FollowButton } from "./FollowButton";

export type AuthorCardProps = {
  author: AuthorInfo;
  clickable?: boolean;
  showAttribution?: boolean;
  showAiBadge?: boolean;
  class?: string;
  priority?: boolean;
};

const Backdrop = ({ author, priority }: { author: AuthorInfo; priority: boolean }) =>
  author.profileImage?.src ? (
    <img
      src={getImageUrl(author.profileImage.src, author.alias)}
      alt={author.profileImage.alt || `${author.alias} profile`}
      class="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
      fetchpriority={priority ? "high" : undefined}
      loading={priority ? undefined : "lazy"}
    />
  ) : (
    <div
      class="absolute inset-0 transition-transform duration-300 group-hover:scale-110"
      style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
    />
  );

const Buttons = ({ author }: { author: AuthorInfo }) => (
  <div class="absolute top-3 right-2 flex items-center gap-1 z-20">
    <div onClick={(event) => event.preventDefault()}>
      <ShareButton
        url={absoluteUrl(`/authors/${author.alias}`)}
        title={`${author.alias} on !tldr`}
        text={author.tagline}
        class="p-2 rounded-full bg-black/50 hover:bg-black/75 backdrop-blur-sm transition-colors"
        buttonLabel="Share author profile"
        iconSize={16}
        iconClass="text-white"
      />
    </div>
    <div onClick={(event) => event.preventDefault()}>
      <FollowButton alias={author.alias} display="icon" />
    </div>
  </div>
);

const Overlay = ({ author, showAiBadge }: { author: AuthorInfo; showAiBadge: boolean }) => (
  <div class="z-20 absolute bottom-0 left-0 right-0 p-4">
    <div class="flex items-center gap-2 mb-2">
      <AuthorProfileImage author={author} size="sm" />
      <div class="flex-1 min-w-0 flex items-center gap-2">
        <h2 class="font-bold text-xl text-white">{author.alias}</h2>
        {showAiBadge && <AiBadge class="text-white" />}
      </div>
    </div>
    <p class="text-white/90 text-xs italic mb-2">{author.tagline}</p>
    <p class="text-white/90 text-xs">
      {author.publishedArticleCount || 0} {author.publishedArticleCount === 1 ? "article" : "articles"} · {author.followerCount || 0}{" "}
      {author.followerCount === 1 ? "follower" : "followers"}
    </p>
  </div>
);

const Hero = ({ author, priority, showAiBadge }: { author: AuthorInfo; priority: boolean; showAiBadge: boolean }) => (
  <div class="relative h-64 [clip-path:inset(0_round_0.75rem_0.75rem_0_0)]">
    <Backdrop author={author} priority={priority} />
    <div class="absolute inset-0 z-10 bg-gradient-to-t from-stone-800/80" />
    <div class="absolute top-4 left-4">
      <span class="bg-black/50 px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm text-white">
        {getCategoryAndSubcategory(author.expertise.category, author.expertise.subcategory).subcategory.name}
      </span>
    </div>
    <Buttons author={author} />
    <Overlay author={author} showAiBadge={showAiBadge} />
  </div>
);

const SUBJECT_CLASS =
  "inline-block text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/30 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700";

const Mission = ({ author }: { author: AuthorInfo }) => (
  <div class="p-3 flex-1 flex flex-col">
    <p class="text-gray-600 dark:text-gray-300 text-sm mb-3">{author.mission}</p>
    <div class="mt-auto flex flex-wrap gap-1 border-t border-gray-200 dark:border-gray-700 pt-3">
      {author.expertise.subjects.map((subject) => (
        <span key={subject} class={SUBJECT_CLASS}>
          {subject}
        </span>
      ))}
    </div>
  </div>
);

export const AuthorCard = ({
  author,
  clickable = true,
  showAttribution = false,
  showAiBadge = false,
  class: className = "",
  priority = false,
}: AuthorCardProps) => {
  const attribution = author.profileImage?.attributionHtml;
  const content = (
    <div class={`group bg-white dark:bg-neutral-800 rounded-xl shadow-md shadow-gray-500/30 p-3 text-left flex flex-col ${className}`}>
      <div class="relative -m-3 mb-0 flex-1 flex flex-col">
        <Hero author={author} priority={priority} showAiBadge={showAiBadge} />
        {showAttribution && attribution && (
          <div class="px-4 py-0 text-gray-500 bg-white dark:bg-neutral-800 text-xs text-center">
            <small class="unsplash-attribution" dangerouslySetInnerHTML={{ __html: attribution.replaceAll("concisely", "nottldr") }} />
          </div>
        )}
        <Mission author={author} />
      </div>
    </div>
  );

  return clickable ? (
    <a href={`/authors/${author.alias}`} class="block h-full" data-test-click="author-card" data-test-author={author.alias}>
      {content}
    </a>
  ) : (
    content
  );
};
